import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Trash2, Eye, EyeOff, Pencil, Search, Loader2, RefreshCw, MessageSquare, CheckSquare, Square, X as XIcon, Download
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AdminGuestBookManager
 *
 * Full CMS for guest wishes:
 *   • List all wishes with status badge (Published / Hidden)
 *   • Per-row: Edit, Publish/Hide toggle, Delete (with confirm dialog)
 *   • Bulk: select multiple → Delete / Hide / Publish
 *   • Search by name/message + filter by status
 *
 * Requires an `authHeader` prop returning a valid HTTP Basic header.
 */
const AdminGuestBookManager = ({ authHeader }) => {
  const [wishes, setWishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Filters (server-side)
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Edit dialog
  const [editTarget, setEditTarget] = useState(null); // full wish or null
  const [editName, setEditName] = useState('');
  const [editMessage, setEditMessage] = useState('');
  const [editReply, setEditReply] = useState('');
  const [editReplyOriginal, setEditReplyOriginal] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null); // wish or { bulk: true, count }
  const [deleting, setDeleting] = useState(false);

  // Export
  const [exporting, setExporting] = useState(false);

  const fetchWishes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API}/admin/guestbook`, {
        headers: authHeader(),
        params,
      });
      setWishes(res.data || []);
    } catch (err) {
      console.error('Failed to load wishes', err);
    } finally {
      setLoading(false);
    }
  }, [authHeader, search, statusFilter]);

  useEffect(() => {
    fetchWishes();
  }, [fetchWishes]);

  // Debounce search input → server call
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Clear stale selections whenever list changes
  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set();
      const alive = new Set(wishes.map((w) => w.id));
      prev.forEach((id) => { if (alive.has(id)) next.add(id); });
      return next;
    });
  }, [wishes]);

  const allVisibleSelected = useMemo(() => (
    wishes.length > 0 && wishes.every((w) => selectedIds.has(w.id))
  ), [wishes, selectedIds]);

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(wishes.map((w) => w.id)));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openEdit = (wish) => {
    setEditTarget(wish);
    setEditName(wish.name || '');
    setEditMessage(wish.message || '');
    setEditReply(wish.reply || '');
    setEditReplyOriginal(wish.reply || '');
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    const name = editName.trim();
    const message = editMessage.trim();
    if (!name || !message) return;
    setEditSaving(true);
    try {
      const payload = { name, message };
      // Only send reply if it changed — empty string clears an existing reply.
      if (editReply !== editReplyOriginal) {
        payload.reply = editReply.trim();
      }
      await axios.patch(`${API}/admin/guestbook/${editTarget.id}`,
        payload,
        { headers: authHeader() }
      );
      setEditTarget(null);
      await fetchWishes();
    } catch (err) {
      console.error('Failed to save edit', err);
      alert(err?.response?.data?.detail || 'Failed to save changes');
    } finally {
      setEditSaving(false);
    }
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API}/admin/guestbook/export.pdf`, {
        headers: authHeader(),
        params,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const stamp = new Date().toISOString().slice(0, 10).replaceAll('-', '');
      const link = document.createElement('a');
      link.href = url;
      link.download = `mary-arrington-guest-wishes-${stamp}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      console.error('Export failed', err);
      alert('Could not generate the PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const toggleStatus = async (wish) => {
    const nextStatus = wish.status === 'published' ? 'hidden' : 'published';
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/guestbook/${wish.id}`,
        { status: nextStatus },
        { headers: authHeader() }
      );
      await fetchWishes();
    } catch (err) {
      console.error('Failed to change status', err);
      alert(err?.response?.data?.detail || 'Failed to change status');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.bulk) {
        await axios.post(`${API}/admin/guestbook/bulk`,
          { ids: Array.from(selectedIds), action: 'delete' },
          { headers: authHeader() }
        );
        setSelectedIds(new Set());
      } else {
        await axios.delete(`${API}/admin/guestbook/${deleteTarget.id}`, {
          headers: authHeader(),
        });
      }
      setDeleteTarget(null);
      await fetchWishes();
    } catch (err) {
      console.error('Failed to delete', err);
      alert(err?.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const bulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    if (action === 'delete') {
      setDeleteTarget({ bulk: true, count: selectedIds.size });
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/guestbook/bulk`,
        { ids: Array.from(selectedIds), action },
        { headers: authHeader() }
      );
      setSelectedIds(new Set());
      await fetchWishes();
    } catch (err) {
      console.error('Bulk action failed', err);
      alert(err?.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBusy(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return '';
    try {
      return new Date(value).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return String(value); }
  };

  return (
    <div className="space-y-4" data-testid="admin-guestbook-manager">
      {/* Toolbar: search + filter + refresh */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-[#8A7A7A] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name or message…"
            className="pl-9 bg-[#F8F5EF] border-[#E5E0D8]"
            data-testid="guestbook-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] bg-[#F8F5EF] border-[#E5E0D8]" data-testid="guestbook-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="hidden">Hidden</SelectItem>
            </SelectContent>
          </Select>
          <button
            onClick={exportPDF}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white hover:bg-[#F8F5EF] text-[#3B2F2F] transition-colors disabled:opacity-60"
            title="Download filtered wishes as a keepsake PDF"
            data-testid="guestbook-export-btn"
          >
            {exporting
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Download className="w-4 h-4" />}
            <span className="hidden sm:inline text-sm">Export PDF</span>
          </button>
          <button
            onClick={fetchWishes}
            className="p-2 rounded-lg border border-[#E5E0D8] hover:bg-[#F8F5EF] transition-colors"
            title="Refresh"
            data-testid="guestbook-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 text-[#3B2F2F] ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="bg-[#3B2F2F] text-white rounded-xl shadow-sm p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="guestbook-bulk-bar">
          <div className="text-sm">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => bulkAction('publish')}
              data-testid="guestbook-bulk-publish"
            >
              <Eye className="w-4 h-4 mr-1" /> Publish
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => bulkAction('hide')}
              data-testid="guestbook-bulk-hide"
            >
              <EyeOff className="w-4 h-4 mr-1" /> Hide
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={busy}
              onClick={() => bulkAction('delete')}
              data-testid="guestbook-bulk-delete"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/10"
              onClick={() => setSelectedIds(new Set())}
            >
              <XIcon className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {/* Select all header */}
        <div className="hidden md:flex items-center gap-3 px-4 py-3 border-b border-[#E5E0D8] bg-[#F8F5EF] text-[#5A4A4A] text-sm">
          <button
            onClick={toggleSelectAll}
            className="p-1 hover:text-[#C8A951]"
            title={allVisibleSelected ? 'Deselect all' : 'Select all'}
            data-testid="guestbook-select-all"
          >
            {allVisibleSelected
              ? <CheckSquare className="w-4 h-4 text-[#C8A951]" />
              : <Square className="w-4 h-4" />}
          </button>
          <span>{wishes.length} wish{wishes.length === 1 ? '' : 'es'}</span>
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center text-[#5A4A4A] gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : wishes.length === 0 ? (
          <div className="p-10 text-center text-[#5A4A4A]">
            <MessageSquare className="w-8 h-8 text-[#C8A951] mx-auto mb-2" />
            No wishes match the current filters.
          </div>
        ) : (
          <ul className="divide-y divide-[#E5E0D8]">
            {wishes.map((wish) => {
              const isSelected = selectedIds.has(wish.id);
              const isPublished = wish.status === 'published';
              return (
                <li
                  key={wish.id}
                  className={`p-4 flex flex-col md:flex-row md:items-start gap-3 transition-colors ${isSelected ? 'bg-[#F8F5EF]' : 'bg-white'}`}
                  data-testid={`guestbook-row-${wish.id}`}
                >
                  <button
                    onClick={() => toggleSelect(wish.id)}
                    className="p-1 self-start"
                    aria-label={isSelected ? 'Deselect' : 'Select'}
                    data-testid={`guestbook-select-${wish.id}`}
                  >
                    {isSelected
                      ? <CheckSquare className="w-4 h-4 text-[#C8A951]" />
                      : <Square className="w-4 h-4 text-[#8A7A7A]" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-medium text-[#3B2F2F]">{wish.name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                        isPublished
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                      data-testid={`guestbook-status-${wish.id}`}
                      >
                        {isPublished ? 'Published' : 'Hidden'}
                      </span>
                      {wish.reply && (
                        <span
                          className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#C8A951]/15 text-[#8A6E1F]"
                          title="You have replied to this wish"
                          data-testid={`guestbook-replied-${wish.id}`}
                        >
                          Replied
                        </span>
                      )}
                      <span className="text-xs text-[#8A7A7A]">
                        {formatDate(wish.submitted_at)}
                        {wish.updated_at ? ` · edited ${formatDate(wish.updated_at)}` : ''}
                      </span>
                    </div>
                    <p className="text-[#5A4A4A] italic whitespace-pre-wrap break-words">
                      “{wish.message}”
                    </p>
                    {wish.reply && (
                      <div className="mt-2 pl-3 border-l-2 border-[#C8A951] bg-[#F8F5EF]/60 rounded-r p-2">
                        <p className="text-sm text-[#3B2F2F] whitespace-pre-wrap break-words">{wish.reply}</p>
                        <p className="text-[11px] text-[#8A6E1F] italic text-right mt-1">
                          — Mary &amp; Arrington{wish.replied_at ? ` · ${formatDate(wish.replied_at)}` : ''}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1 self-end md:self-start shrink-0">
                    <button
                      onClick={() => openEdit(wish)}
                      className="p-2 text-[#3B2F2F] hover:bg-[#F8F5EF] rounded-lg"
                      title="Edit"
                      data-testid={`guestbook-edit-${wish.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => toggleStatus(wish)}
                      disabled={busy}
                      className="p-2 text-[#3B2F2F] hover:bg-[#F8F5EF] rounded-lg"
                      title={isPublished ? 'Hide' : 'Publish'}
                      data-testid={`guestbook-toggle-${wish.id}`}
                    >
                      {isPublished ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setDeleteTarget(wish)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      title="Delete"
                      data-testid={`guestbook-delete-${wish.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => { if (!o) setEditTarget(null); }}>
        <DialogContent data-testid="guestbook-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit wish</DialogTitle>
            <DialogDescription>
              Update the guest name or message. Changes go live immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-[#3B2F2F] font-medium mb-1 block">Name</label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                data-testid="guestbook-edit-name"
              />
            </div>
            <div>
              <label className="text-sm text-[#3B2F2F] font-medium mb-1 block">Message</label>
              <Textarea
                value={editMessage}
                onChange={(e) => setEditMessage(e.target.value)}
                rows={5}
                data-testid="guestbook-edit-message"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm text-[#3B2F2F] font-medium">
                  Reply from Mary &amp; Arrington
                  <span className="ml-2 text-xs font-normal text-[#8A7A7A]">(optional, shown publicly)</span>
                </label>
                {editReply.trim() && (
                  <button
                    type="button"
                    onClick={() => setEditReply('')}
                    className="text-xs text-red-500 hover:underline"
                    data-testid="guestbook-edit-remove-reply"
                  >
                    Remove reply
                  </button>
                )}
              </div>
              <Textarea
                value={editReply}
                onChange={(e) => setEditReply(e.target.value)}
                rows={3}
                placeholder="Write a personal thank-you — signed automatically as “— Mary & Arrington”…"
                data-testid="guestbook-edit-reply"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} disabled={editSaving}>
              Cancel
            </Button>
            <Button
              onClick={saveEdit}
              disabled={editSaving || !editName.trim() || !editMessage.trim()}
              className="bg-[#C8A951] hover:bg-[#B09240] text-white"
              data-testid="guestbook-edit-save"
            >
              {editSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent data-testid="guestbook-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete wish{deleteTarget?.bulk ? 'es' : ''}?</DialogTitle>
            <DialogDescription>
              {deleteTarget?.bulk
                ? `Are you sure you want to permanently delete ${deleteTarget.count} selected wish${deleteTarget.count === 1 ? '' : 'es'}? This action cannot be undone.`
                : 'Are you sure you want to permanently delete this wish? This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="guestbook-delete-confirm"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGuestBookManager;
